/**
 * Device identity and the local-state wipe.
 *
 * `localStorage` holds the device UUID and UI preferences and nothing else. It is a hint the
 * server uses to recognise a returning browser — never a credential. Anything that grants access
 * lives in an HttpOnly cookie the page cannot read, or in memory that dies with the tab.
 */

import { uuidv7 } from '../lib/uuid.js';

const DEVICE_KEY = 'pos.device.id';
const LABEL_KEY = 'pos.device.label';

/** Keys the wipe deliberately leaves alone: they are preferences, not session. */
const KEEP_ON_WIPE = new Set([DEVICE_KEY, LABEL_KEY, 'pos.theme']);

const safeGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    // Private mode, or storage disabled by policy. The app still works; the device just looks
    // new on every visit, which costs a password login rather than a crash.
    return null;
  }
};

const safeSet = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* see safeGet */
  }
};

/** Stable per browser profile. Created once and never rotated — rotating it looks like a new device. */
export function deviceId(): string {
  const existing = safeGet(DEVICE_KEY);
  if (existing !== null && existing.length > 0) return existing;

  // Not `crypto.randomUUID()`: browsers only expose that in a secure context, so a till reached
  // over plain http — an IP address during setup, a LAN box with no certificate — threw on the
  // very first request. `getRandomValues` underneath `uuidv7` has no such restriction.
  const created = uuidv7();
  safeSet(DEVICE_KEY, created);
  return created;
}

/** What the owner sees in the device list. Guessed once, editable later from the profile screen. */
export function deviceLabel(): string {
  const existing = safeGet(LABEL_KEY);
  if (existing !== null && existing.length > 0) return existing;

  const guess = guessLabel();
  safeSet(LABEL_KEY, guess);
  return guess;
}

export function setDeviceLabel(label: string): void {
  safeSet(LABEL_KEY, label.trim().slice(0, 60));
}

export function platform(): string {
  // `userAgentData` where it exists, because `navigator.platform` is deprecated and frozen —
  // it reports "Win32" on 64-bit Windows and lies about most things it is asked.
  const data = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData;
  const reported = data?.platform;

  return reported !== undefined && reported.length > 0 ? reported : 'web';
}

function guessLabel(): string {
  const ua = navigator.userAgent;
  if (/android/i.test(ua)) return 'Android phone';
  if (/iphone|ipad/i.test(ua)) return 'iPhone';
  if (/windows/i.test(ua)) return 'Windows PC';
  if (/mac os/i.test(ua)) return 'Mac';
  return 'Browser';
}

/**
 * Called on `DEVICE_REVOKED` and on five failed PIN attempts.
 *
 * Everything the previous session could see has to go, including the cached catalogue: it carries
 * per-shop stock and prices, and the person now holding the device is not necessarily the person
 * who was signed in. The device id survives so the owner's device list still makes sense.
 */
export async function wipeLocalState(): Promise<void> {
  try {
    const keys = Object.keys(localStorage).filter((key) => !KEEP_ON_WIPE.has(key));
    for (const key of keys) localStorage.removeItem(key);
  } catch {
    /* see safeGet */
  }

  try {
    sessionStorage.clear();
  } catch {
    /* see safeGet */
  }

  await deleteAllDatabases();
}

async function deleteAllDatabases(): Promise<void> {
  try {
    // `databases()` is missing on Firefox, so the call is guarded and the known name is added
    // explicitly below either way.
    const listed =
      typeof indexedDB.databases === 'function' ? await indexedDB.databases() : [];
    const names = new Set(
      listed.map((entry) => entry.name).filter((name): name is string => name !== undefined && name.length > 0),
    );
    names.add('pos-catalogue');

    await Promise.all(
      [...names].map(
        (name) =>
          new Promise<void>((resolve) => {
            const request = indexedDB.deleteDatabase(name);
            // Resolve on every outcome, blocked included: a wipe that hangs because another tab
            // holds the database open would leave the user staring at a dead screen.
            request.onsuccess = request.onerror = request.onblocked = () => {
              resolve();
            };
          }),
      ),
    );
  } catch {
    /* nothing recoverable; the session is already gone */
  }
}
